import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Layout from "../components/layout/Layout";
import { useAuth, useDestination, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { TrustBadges } from "../components/TrustBadges";
import { formatMoney } from "../lib/safeData";
import {
  CreditCard,
  Package,
  Loader2,
  ShieldCheck,
  Building2,
  Banknote,
  Upload,
  CheckCircle2,
  AlertCircle,
  Truck,
  Clock,
  Zap,
  Info,
  RefreshCw,
} from "lucide-react";
import LuxuryProductImage from "../components/LuxuryProductImage";
import ConciergeCartItem, {
  isConciergeCartItem,
  mapCartLineToConciergeItem,
} from "../components/ConciergeCartItem";
import DualLandedPrice from "../components/DualLandedPrice";
import DynamicCheckoutPriceCard from "../components/DynamicCheckoutPriceCard";
import { buildCheckoutPriceSummary } from "../services/dhcFinancialEngine";
import { buildGlobalCheckoutSummary } from "../services/globalFinancialEngineCore";
import { useGlobalCartPricing } from "../hooks/useGlobalCartPricing";
import { fetchPickupQuote } from "../services/shippingApi";
import { MapPin } from "lucide-react";
import {
  listShopDestinations,
  getDestinationConfig,
  listCaribbeanOnly,
} from "../config/caribbeanDestinations";

const destinations = listShopDestinations();
const caribbeanDestinations = listCaribbeanOnly();

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { destination, currency, shippingSpeed } = useDestination();

  const [cart, setCart] = useState(null);
  const pricedCart = useGlobalCartPricing(cart, destination);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderCreated, setOrderCreated] = useState(null);
  
  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("paypal");
  const [bankDetails, setBankDetails] = useState(null);
  
  // Bank transfer / FirstPay form
  const [payerName, setPayerName] = useState(user?.name || "");
  const [transferReference, setTransferReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Shipping Address
  const [address, setAddress] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    street: "",
    city: "",
    state: "",
    postal_code: "",
    country: destination,
  });

  // Delivery Method: "standard" | "DIRECT" | "HUB_STAY"
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [pickupQuote, setPickupQuote] = useState(null);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [pickupError, setPickupError] = useState(null);
  const [selectedPickupRate, setSelectedPickupRate] = useState(null);

  // Barbados parish delivery
  const [parishFees, setParishFees] = useState(null);
  const [selectedParish, setSelectedParish] = useState("COLLECT");
  const destinationConfig = getDestinationConfig(address.country);
  const isBarbados = destinationConfig?.hubClearance === true;
  const customsConfig = destinationConfig?.customs;

  // Barbados TAMIS or other Caribbean customs / tax ID
  const [tamisId, setTamisId] = useState("");
  const [customsReference, setCustomsReference] = useState("");

  const parishDeliveryFee =
    isBarbados &&
    deliveryMethod !== "standard" &&
    selectedParish !== "COLLECT" &&
    parishFees?.parishes
      ? parishFees.parishes.find((p) => p.parish === selectedParish)?.fee || 0
      : 0;

  const expressShippingFee =
    deliveryMethod !== "standard" && selectedPickupRate?.combined_total
      ? Number(selectedPickupRate.combined_total)
      : 0;

  const checkoutTotal =
    (pricedCart?.total || 0) + expressShippingFee + parishDeliveryFee;

  const fetchCart = useCallback(async () => {
    // `loading` starts true and is cleared below, so switching payment method
    // refreshes the summary in place instead of flashing the page skeleton.
    try {
      const response = await axios.get(`${API}/cart`, {
        params: {
          destination,
          currency,
          shipping_speed: shippingSpeed,
          // Processing fees differ per method, and the order locks the rate for
          // whichever one is chosen — so the summary must price that method.
          payment_method: selectedMethod,
        },
        withCredentials: true,
      });
      if (!response.data.items?.length) {
        toast.info("Your cart is empty");
        navigate("/cart");
        return;
      }
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast.error("Failed to load cart");
      navigate("/cart");
    } finally {
      setLoading(false);
    }
  }, [destination, currency, shippingSpeed, selectedMethod, navigate]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/methods`, {
        params: { destination },
        withCredentials: true,
      });
      setPaymentMethods(response.data.methods || []);
      const bankMethod = response.data.methods?.find(m => m.id === "bank_transfer");
      if (bankMethod?.bank_details) {
        setBankDetails(bankMethod.bank_details);
      } else {
        setBankDetails(null);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setPaymentMethods([{
        id: "paypal",
        name: "PayPal",
        description: "Pay instantly with PayPal",
        type: "instant"
      }]);
    }
  }, [destination]);

  useEffect(() => {
    fetchCart();
    fetchPaymentMethods();
  }, [fetchCart, fetchPaymentMethods]);

  // Update address country when destination changes
  useEffect(() => {
    setAddress(prev => ({ ...prev, country: destination }));
  }, [destination]);

  // Fetch Barbados parish fees when destination is BB
  useEffect(() => {
    if (destination !== "BB") { setParishFees(null); return; }
    axios.get(`${API}/shipping/parish-fees`, { withCredentials: true })
      .then(res => setParishFees(res.data))
      .catch(() => setParishFees(null));
  }, [destination]);

  const loadPickupQuote = useCallback(async (method, addressSnapshot) => {
    if (method === "standard") return;

    const totalWeight = pricedCart?.items?.reduce(
      (sum, item) => sum + (item.weight_lbs || 2) * 0.4536 * (item.quantity || 1),
      0
    ) || 2;

    setPickupLoading(true);
    setPickupError(null);
    try {
      const quote = await fetchPickupQuote({
        destinationCountry: addressSnapshot.country,
        destinationPostal: addressSnapshot.postal_code,
        destinationCity: addressSnapshot.city,
        destinationState: addressSnapshot.state,
        streetLines: addressSnapshot.street ? [addressSnapshot.street] : [""],
        weightKg: Math.max(totalWeight, 0.5),
        shippingPreference: method,
      });
      setPickupQuote(quote);
      if (quote.rates?.length) {
        setSelectedPickupRate(quote.rates[0]);
      } else {
        setSelectedPickupRate(null);
      }
    } catch (err) {
      setPickupQuote(null);
      setSelectedPickupRate(null);
      setPickupError(
        err.response?.data?.detail || err.response?.data?.error || "Could not fetch delivery quote"
      );
    } finally {
      setPickupLoading(false);
    }
  }, [pricedCart?.items]);

  const handleDeliveryMethodChange = async (method) => {
    setDeliveryMethod(method);
    setPickupQuote(null);
    setSelectedPickupRate(null);
    setPickupError(null);

    if (method === "standard") return;
    await loadPickupQuote(method, address);
  };

  // Re-fetch carrier rates when address changes during express/hub checkout
  useEffect(() => {
    if (deliveryMethod === "standard") return;
    if (!address.country || !address.city) return;

    const timer = setTimeout(() => {
      loadPickupQuote(deliveryMethod, address);
    }, 600);

    return () => clearTimeout(timer);
  }, [
    address.country,
    address.city,
    address.state,
    address.postal_code,
    address.street,
    deliveryMethod,
    loadPickupQuote,
  ]);

  const handleAddressChange = (e) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  const validateAddress = () => {
    const required = ["name", "email", "phone", "street", "city", "country"];
    for (const field of required) {
      if (!address[field]) {
        toast.error(`Please fill in ${field.replace("_", " ")}`);
        return false;
      }
    }
    if (customsConfig?.required) {
      if (customsConfig.type === "tamis" && !/^\d{13}$/.test(tamisId.trim())) {
        toast.error("A valid 13-digit TAMIS ID is required for Barbados orders");
        return false;
      }
      if (customsConfig.type === "trn" && !/^\d{9}$/.test(customsReference.trim())) {
        toast.error("A valid 9-digit Jamaica TRN is required");
        return false;
      }
      if (
        customsConfig.type === "customs_reference" &&
        !/^[A-Za-z0-9-]{5,30}$/.test(customsReference.trim())
      ) {
        toast.error(`A valid ${customsConfig.label} is required for ${destinationConfig?.name}`);
        return false;
      }
    }
    return true;
  };

  const createOrder = async (paymentMethod = "paypal") => {
    if (!validateAddress()) return null;
    if (deliveryMethod !== "standard" && !selectedPickupRate) {
      toast.error("Select a FedEx or DHL shipping option, or use Standard Shipping");
      return null;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API}/orders/create`,
        {
          shipping_address: address,
          shipping_speed: shippingSpeed,
          payment_method: paymentMethod,
          tamis_id: customsConfig?.type === "tamis" ? tamisId.trim() : null,
          customs_reference:
            customsConfig?.type !== "tamis" && customsConfig?.required
              ? customsReference.trim()
              : null,
          delivery_method: deliveryMethod,
          shipping_carrier: selectedPickupRate?.carrier || null,
          shipping_service: selectedPickupRate?.service || null,
          shipping_fee: deliveryMethod !== "standard" ? expressShippingFee : 0,
          parish_delivery_fee: parishDeliveryFee,
          parish: isBarbados && selectedParish !== "COLLECT" ? selectedParish : null,
          shipping_quote: selectedPickupRate || null,
        },
        {
          params: { destination, currency },
          withCredentials: true,
        }
      );
      setOrderCreated(response.data);
      return response.data;
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
      return null;
    } finally {
      setProcessing(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    const order = orderCreated || await createOrder("paypal");
    if (!order) {
      toast.error("Failed to create order. Please try again.");
      throw new Error("Failed to create order");
    }

    try {
      const response = await axios.post(
        `${API}/orders/${order.order_id}/pay`,
        {},
        { withCredentials: true }
      );
      return response.data.paypal_order_id;
    } catch (error) {
      console.error("Error creating PayPal order:", error);
      toast.error(error.response?.data?.detail || "Could not start PayPal checkout. Please try again.");
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      setProcessing(true);
      const captureRes = await axios.post(
        `${API}/orders/${orderCreated.order_id}/capture`,
        { paypal_order_id: data.orderID },
        { withCredentials: true }
      );
      toast.success("Payment successful!");
      if (captureRes.data?.showcase_notice) {
        toast.info(captureRes.data.showcase_notice, { duration: 8000 });
      }
      navigate(`/order-confirmation?order_id=${orderCreated.order_id}`, {
        state: { showcaseNotice: captureRes.data?.showcase_notice },
      });
    } catch (error) {
      console.error("Error capturing payment:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match(/^(image\/|application\/pdf)/)) {
        toast.error("Please upload an image or PDF file");
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      
      setReceiptFile(file);
      
      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleBankTransferSubmit = async () => {
    if (!payerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!transferReference.trim()) {
      toast.error("Please enter the transfer reference");
      return;
    }

    try {
      setProcessing(true);
      
      // Create order first
      const order = orderCreated || await createOrder("bank_transfer");
      if (!order) return;

      // Initiate bank transfer
      const response = await axios.post(
        `${API}/payments/bank-transfer/initiate`,
        {
          order_id: order.order_id,
          payer_name: payerName,
          transfer_reference: transferReference,
        },
        { withCredentials: true }
      );

      const paymentId = response.data.payment_id;

      // Upload receipt if provided (await so user sees loading state)
      if (receiptFile) {
        setUploadingReceipt(true);
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(receiptFile);
          });
          await axios.post(
            `${API}/payments/${paymentId}/upload-receipt`,
            { receipt_base64: base64 },
            { withCredentials: true }
          );
        } catch (uploadErr) {
          console.error("Error uploading receipt:", uploadErr);
          toast.error("Receipt upload failed, but payment was recorded. We may contact you for verification.");
        } finally {
          setUploadingReceipt(false);
        }
      }

      toast.success("Payment submitted for verification!");
      navigate(`/order-confirmation?order_id=${order.order_id}`);
    } catch (error) {
      console.error("Error submitting bank transfer:", error);
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleFirstPaySubmit = async () => {
    if (!payerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!transferReference.trim()) {
      toast.error("Please enter the transaction ID");
      return;
    }

    try {
      setProcessing(true);
      
      // Create order first
      const order = orderCreated || await createOrder("firstpay");
      if (!order) return;

      // Initiate FirstPay payment
      const response = await axios.post(
        `${API}/payments/firstpay/initiate`,
        {
          order_id: order.order_id,
          payer_name: payerName,
          transaction_id: transferReference,
        },
        { withCredentials: true }
      );

      const paymentId = response.data.payment_id;

      // Upload receipt if provided (await so user sees loading state)
      if (receiptFile) {
        setUploadingReceipt(true);
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(receiptFile);
          });
          await axios.post(
            `${API}/payments/${paymentId}/upload-receipt`,
            { receipt_base64: base64 },
            { withCredentials: true }
          );
        } catch (uploadErr) {
          console.error("Error uploading receipt:", uploadErr);
          toast.error("Receipt upload failed, but payment was recorded. We may contact you for verification.");
        } finally {
          setUploadingReceipt(false);
        }
      }

      toast.success("Payment submitted for verification!");
      navigate(`/order-confirmation?order_id=${order.order_id}`);
    } catch (error) {
      console.error("Error submitting FirstPay:", error);
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price) => formatMoney(price, currency);

  const getMethodIcon = (methodId) => {
    switch (methodId) {
      case "paypal":
        return <CreditCard className="h-5 w-5" />;
      case "bank_transfer":
        return <Building2 className="h-5 w-5" />;
      case "firstpay":
        return <Banknote className="h-5 w-5" />;
      default:
        return <CreditCard className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" data-testid="checkout-title">
          <CreditCard className="inline h-8 w-8 mr-3" />
          Checkout
        </h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Shipping Address Form */}
          <div className="space-y-6">
            <div className="container-card p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">
                Shipping Address
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={address.name}
                      onChange={handleAddressChange}
                      placeholder="John Doe"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={address.phone}
                      onChange={handleAddressChange}
                      placeholder="+1 234 567 8900"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={address.email}
                    onChange={handleAddressChange}
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="street">Street Address</Label>
                  <Input
                    id="street"
                    name="street"
                    value={address.street}
                    onChange={handleAddressChange}
                    placeholder="123 Main Street"
                    data-testid="input-street"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={address.city}
                      onChange={handleAddressChange}
                      placeholder="Bridgetown"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State/Parish</Label>
                    <Input
                      id="state"
                      name="state"
                      value={address.state}
                      onChange={handleAddressChange}
                      placeholder="St. Michael"
                      data-testid="input-state"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={address.postal_code}
                      onChange={handleAddressChange}
                      placeholder="BB11000"
                      data-testid="input-postal"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Select
                      value={address.country}
                      onValueChange={(value) =>
                        setAddress({ ...address, country: value })
                      }
                    >
                      <SelectTrigger data-testid="input-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_caribbean_header" disabled>
                          Caribbean
                        </SelectItem>
                        {caribbeanDestinations.map((dest) => (
                          <SelectItem key={dest.code} value={dest.code}>
                            {dest.flag} {dest.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="_intl_header" disabled>
                          International
                        </SelectItem>
                        {destinations
                          .filter((d) => d.region === "international")
                          .map((dest) => (
                            <SelectItem key={dest.code} value={dest.code}>
                              {dest.flag} {dest.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Customs / import ID — required per destination */}
            {customsConfig?.required && (
              <div className="container-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-lg font-bold text-slate-900">
                    {destinationConfig?.name} Customs
                  </h2>
                </div>
                <p className="text-xs text-slate-500 mb-3">{customsConfig.hint}</p>
                <div>
                  <Label htmlFor="customs_field">{customsConfig.label}</Label>
                  {customsConfig.type === "tamis" ? (
                    <>
                      <Input
                        id="customs_field"
                        name="tamis_id"
                        value={tamisId}
                        onChange={(e) =>
                          setTamisId(e.target.value.replace(/\D/g, "").slice(0, 13))
                        }
                        placeholder="1234567890123"
                        maxLength={13}
                        inputMode="numeric"
                        data-testid="input-tamis"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-400">{tamisId.length}/13 digits</span>
                        {tamisId.length === 13 && (
                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Valid format
                          </span>
                        )}
                      </div>
                    </>
                  ) : customsConfig.type === "trn" ? (
                    <Input
                      id="customs_field"
                      name="customs_reference"
                      value={customsReference}
                      onChange={(e) =>
                        setCustomsReference(e.target.value.replace(/\D/g, "").slice(0, 9))
                      }
                      placeholder="123456789"
                      maxLength={9}
                      inputMode="numeric"
                      data-testid="input-customs"
                    />
                  ) : (
                    <Input
                      id="customs_field"
                      name="customs_reference"
                      value={customsReference}
                      onChange={(e) => setCustomsReference(e.target.value.slice(0, 30))}
                      placeholder="Importer / tax reference"
                      data-testid="input-customs"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Delivery Method Selection */}
            <div className="container-card p-6">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-5 w-5 text-orange-500" />
                <h2 className="text-lg font-bold text-slate-900">Delivery Method</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Choose how you&apos;d like your package delivered. Direct Express quotes FedEx and DHL live rates.
              </p>

              <div className="space-y-3 mb-4">
                {/* Standard */}
                <button
                  type="button"
                  onClick={() => handleDeliveryMethodChange("standard")}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                    deliveryMethod === "standard"
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="delivery-standard"
                >
                  <div className={`mt-0.5 p-1.5 rounded-full ${deliveryMethod === "standard" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Package className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">Standard Shipping</p>
                    <p className="text-xs text-slate-500 mt-0.5">Calculated at checkout based on your destination.</p>
                  </div>
                  {deliveryMethod === "standard" && <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />}
                </button>

                {/* Direct Express */}
                <button
                  type="button"
                  onClick={() => handleDeliveryMethodChange("DIRECT")}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                    deliveryMethod === "DIRECT"
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="delivery-direct"
                >
                  <div className={`mt-0.5 p-1.5 rounded-full ${deliveryMethod === "DIRECT" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Zap className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">Direct Express</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Fastest &mdash; Ships from vendor directly to your address via FedEx.
                    </p>
                  </div>
                  {deliveryMethod === "DIRECT" && <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />}
                </button>

                {/* Mansion Certified Pickup */}
                <button
                  type="button"
                  onClick={() => handleDeliveryMethodChange("HUB_STAY")}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                    deliveryMethod === "HUB_STAY"
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="delivery-hub"
                >
                  <div className={`mt-0.5 p-1.5 rounded-full ${deliveryMethod === "HUB_STAY" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">Mansion Certified Pickup</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Safest &mdash; Ships to DHC Barbados HQ for quality inspection &amp; local pickup/delivery.
                    </p>
                  </div>
                  {deliveryMethod === "HUB_STAY" && <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />}
                </button>
              </div>

              {/* Quote results for DIRECT / HUB_STAY */}
              {/* Barbados Parish Delivery Selector */}
              {isBarbados && deliveryMethod !== "standard" && parishFees && (
                <div className="space-y-3 border-t border-slate-100 pt-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    <p className="text-sm font-semibold text-slate-900">Barbados Delivery</p>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    All Barbados orders arrive at our Culloden Road HQ ({parishFees.hub?.postal}).
                    Choose how you&apos;d like to receive your package:
                  </p>

                  <button
                    type="button"
                    onClick={() => setSelectedParish("COLLECT")}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all ${
                      selectedParish === "COLLECT"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Collect at Culloden Road HQ</p>
                      <p className="text-xs text-slate-500 mt-0.5">{parishFees.hub?.address}</p>
                    </div>
                    <p className="text-sm font-bold text-emerald-600">FREE</p>
                  </button>

                  {parishFees.parishes?.map((p) => (
                    <button
                      key={p.parish}
                      type="button"
                      onClick={() => setSelectedParish(p.parish)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all ${
                        selectedParish === p.parish
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Private Delivery &ndash; {p.parish}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{p.zone} zone</p>
                      </div>
                      <p className="text-sm font-bold text-slate-900">${p.fee.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              )}

              {deliveryMethod !== "standard" && (
                <div className="space-y-3 border-t border-slate-100 pt-4">
                  {pickupLoading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calculating {deliveryMethod === "HUB_STAY" ? "hub" : "direct"} rates&hellip;
                    </div>
                  )}

                  {pickupError && (
                    <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
                      <div className="flex items-start gap-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{pickupError}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeliveryMethodChange(deliveryMethod)}
                          disabled={pickupLoading}
                          className="border-red-200 text-red-700 hover:bg-red-100"
                        >
                          <RefreshCw className="h-4 w-4 mr-1.5" />
                          Retry quote
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeliveryMethodChange("standard")}
                          className="border-slate-200 text-slate-700 hover:bg-slate-100"
                        >
                          <Package className="h-4 w-4 mr-1.5" />
                          Use Standard Shipping instead
                        </Button>
                      </div>
                    </div>
                  )}

                  {pickupQuote && !pickupLoading && (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        {pickupQuote.address_validated && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Address verified
                          </span>
                        )}
                        {pickupQuote.providers?.length > 1 && (
                          <span className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            FedEx + DHL rates
                          </span>
                        )}
                        {pickupQuote.is_caribbean_route && (
                          <span className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                            Caribbean Route
                          </span>
                        )}
                        {pickupQuote.shipping_preference === "HUB_STAY" && (
                          <span className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                            DHC Hub Inspection
                          </span>
                        )}
                      </div>

                      {pickupQuote.carrier_summary && (
                        <p className="text-xs text-slate-600 mt-1">
                          Choose your carrier —{" "}
                          {pickupQuote.carrier_summary.fedex?.available && (
                            <span className="font-medium text-purple-700">FedEx</span>
                          )}
                          {pickupQuote.carrier_summary.fedex?.available &&
                            pickupQuote.carrier_summary.dhl?.available && " or "}
                          {pickupQuote.carrier_summary.dhl?.available && (
                            <span className="font-medium text-yellow-700">DHL</span>
                          )}
                          {pickupQuote.carrier_summary.fedex?.available &&
                            pickupQuote.carrier_summary.dhl?.available &&
                            " — both quoted for your route."}
                        </p>
                      )}

                      <div className="space-y-4 mt-2">
                        {["FedEx", "DHL"].map((carrierLabel) => {
                          const carrierRates = (pickupQuote.rates || []).filter((rate) =>
                            (rate.carrier || "FedEx").toUpperCase().includes(carrierLabel)
                          );
                          if (!carrierRates.length) return null;

                          return (
                            <div key={carrierLabel} className="space-y-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {carrierLabel} options
                              </p>
                              {carrierRates.map((rate) => {
                                const combined = Number(rate.combined_total) || 0;
                                const pickupFee = Number(rate.pickup_fee) || 0;
                                const hubFee = Number(rate.hub_surcharge) || 0;
                                const isSelected =
                                  selectedPickupRate?.service === rate.service &&
                                  (selectedPickupRate?.carrier || "FedEx") === (rate.carrier || "FedEx");

                                return (
                                  <button
                                    key={`${rate.carrier || carrierLabel}-${rate.service}`}
                                    type="button"
                                    onClick={() => setSelectedPickupRate(rate)}
                                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all ${
                                      isSelected
                                        ? "border-orange-500 bg-orange-50"
                                        : "border-slate-200 hover:border-slate-300"
                                    }`}
                                  >
                                    <div>
                                      <p className="text-sm font-semibold text-slate-900">
                                        {rate.name}
                                      </p>
                                      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 mt-0.5">
                                        <span className="inline-flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {rate.transit_days !== "N/A"
                                            ? `${rate.transit_days} business days`
                                            : "Transit time TBD"}
                                        </span>
                                        {rate.is_live ? (
                                          <span className="text-emerald-600 font-medium">Live rate</span>
                                        ) : (
                                          <span className="text-amber-600 font-medium">Estimated</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-bold text-slate-900">
                                        {rate.currency === "USD" ? "$" : `${rate.currency} `}
                                        {combined.toFixed(2)}
                                      </p>
                                      <p className="text-[10px] text-slate-400">
                                        ${pickupFee.toFixed(2)} pickup
                                        {hubFee > 0 && ` + $${hubFee.toFixed(2)} inspection`}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>

                      {pickupQuote.rates.length === 0 && (
                        <p className="text-sm text-slate-500 py-2">
                          No carrier rates available for this route. Standard shipping will apply.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Order Summary & Payment */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="container-card p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">
                Order Summary
              </h2>

              <div className="space-y-3 max-h-48 overflow-y-auto">
                {pricedCart?.items?.map((item) =>
                  isConciergeCartItem(item) ? (
                    <ConciergeCartItem
                      key={item.product_id}
                      compact
                      item={mapCartLineToConciergeItem(item)}
                      itemTotal={item.item_total}
                      formatPrice={formatPrice}
                      testId={`checkout-item-${item.product_id}`}
                    />
                  ) : (
                  <div
                    key={item.product_id}
                    className="flex items-center gap-3"
                  >
                    <div className="w-12 h-12 bg-slate-100 rounded-sm overflow-hidden">
                      <LuxuryProductImage
                        product={item}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        showBadge={false}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-medium">
                      {formatPrice(item.item_total)}
                    </p>
                  </div>
                  )
                )}
              </div>

              {pricedCart?.items?.some((item) => isConciergeCartItem(item)) && (
                <div className="mt-4">
                  <DynamicCheckoutPriceCard
                    summary={buildGlobalCheckoutSummary({
                      ...pricedCart.items.find((item) => isConciergeCartItem(item)),
                      destination,
                      landed_price: pricedCart.total,
                    })}
                  />
                </div>
              )}

              <div className="border-t border-slate-200 mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Import & Clearance</span>
                  <span className="text-slate-600">Included</span>
                </div>
                {isBarbados && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Customs &amp; clearance (included)</span>
                    <span className="text-emerald-600 font-medium">Included</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Landed & Legal (Pre-paid)</span>
                  <span className="text-slate-600">Included</span>
                </div>
                {deliveryMethod !== "standard" && selectedPickupRate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      {selectedPickupRate.carrier} {selectedPickupRate.name}
                    </span>
                    <span>{formatPrice(expressShippingFee)}</span>
                  </div>
                )}
                {parishDeliveryFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Parish delivery ({selectedParish})</span>
                    <span>{formatPrice(parishDeliveryFee)}</span>
                  </div>
                )}
              </div>

              {/* Frustration Offset tooltip */}
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mt-3">
                <Info className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-emerald-700 leading-relaxed">
                  Includes 100% Guaranteed Landed Cost. No hidden customs calls, no forwarder delays, and direct delivery to Culloden Road HQ.
                </p>
              </div>

              <div className="border-t border-slate-200 mt-4 pt-4">
                <div className="flex justify-between text-xl font-bold items-baseline gap-2">
                  <span>Total</span>
                  <span className="text-emerald-600 text-right" data-testid="checkout-total">
                    {isBarbados ? (
                      <DualLandedPrice
                        amount={checkoutTotal}
                        size="lg"
                        usdClassName="text-emerald-600"
                      />
                    ) : (
                      formatPrice(checkoutTotal)
                    )}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  All-inclusive landed USD with BBD at official 2:1 peg •{" "}
                  {destinations.find((d) => d.code === destination)?.name || destination}
                </p>
              </div>
            </div>

            {/* Payment Method Selector */}
            <div className="container-card p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">
                Payment Method
              </h2>

              {/* Payment Method Tabs — fallback to PayPal if API returns empty */}
              <div className="grid grid-cols-1 gap-3 mb-6">
                {(paymentMethods.length > 0 ? paymentMethods : [
                  { id: "paypal", name: "PayPal", description: "Pay instantly with PayPal", type: "instant" }
                ]).map((method) => (
                  <button
                    key={method.id}
                    onClick={() => {
                      setSelectedMethod(method.id);
                      setTransferReference("");
                    }}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                      selectedMethod === method.id
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`payment-method-${method.id}`}
                  >
                    <div className={`p-2 rounded-full ${
                      selectedMethod === method.id 
                        ? "bg-orange-500 text-white" 
                        : "bg-slate-100 text-slate-600"
                    }`}>
                      {getMethodIcon(method.id)}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{method.name}</p>
                      <p className="text-xs text-slate-500">{method.description}</p>
                    </div>
                    {selectedMethod === method.id && (
                      <CheckCircle2 className="h-5 w-5 text-orange-500" />
                    )}
                  </button>
                ))}
              </div>

              {processing ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                  <span className="ml-3 text-slate-600">Processing...</span>
                </div>
              ) : (
                <>
                  {/* PayPal */}
                  {selectedMethod === "paypal" && (
                    <div data-testid="paypal-payment-section">
                      <PayPalScriptProvider
                        options={{
                          clientId:
                            process.env.REACT_APP_CHEEPSWEEPZ_PAYPAL_CLIENT_ID ||
                            process.env.REACT_APP_PAYPAL_CLIENT_ID ||
                            "",
                          currency: currency === "BBD" ? "USD" : currency,
                        }}
                      >
                        <PayPalButtons
                          style={{
                            layout: "vertical",
                            color: "black",
                            shape: "rect",
                            label: "pay",
                          }}
                          createOrder={handlePayPalCreateOrder}
                          onApprove={handlePayPalApprove}
                          onError={(err) => {
                            console.error("PayPal error:", err);
                            toast.error("Payment error. Please try again.");
                          }}
                          data-testid="paypal-buttons"
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}

                  {/* Bank Transfer */}
                  {selectedMethod === "bank_transfer" && (
                    <div className="space-y-4" data-testid="bank-transfer-section">
                      {/* Bank Details */}
                      {bankDetails && (
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Bank Details
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Bank:</span>
                              <span className="font-medium">{bankDetails.bank_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Account Name:</span>
                              <span className="font-medium">{bankDetails.account_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Account Number:</span>
                              <span className="font-medium">{bankDetails.account_number}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Branch:</span>
                              <span className="font-medium">{bankDetails.branch}</span>
                            </div>
                            {bankDetails.swift && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">SWIFT:</span>
                                <span className="font-medium">{bankDetails.swift}</span>
                              </div>
                            )}
                          </div>
                          {bankDetails.notes && (
                            <div className="mt-3 p-2 bg-orange-50 rounded text-xs text-orange-700 flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              {bankDetails.notes}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Payer Info Form */}
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="payer_name">Your Name (as on bank account)</Label>
                          <Input
                            id="payer_name"
                            value={payerName}
                            onChange={(e) => setPayerName(e.target.value)}
                            placeholder="John Doe"
                            data-testid="input-payer-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="transfer_ref">Transfer Reference / Transaction ID</Label>
                          <Input
                            id="transfer_ref"
                            value={transferReference}
                            onChange={(e) => setTransferReference(e.target.value)}
                            placeholder="Enter your bank transfer reference"
                            data-testid="input-transfer-ref"
                          />
                        </div>
                        <div>
                          <Label htmlFor="receipt">Upload Receipt (optional)</Label>
                          <div className="mt-1">
                            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 transition-colors">
                              <input
                                type="file"
                                id="receipt"
                                className="hidden"
                                accept="image/*,.pdf"
                                onChange={handleFileChange}
                                data-testid="input-receipt"
                              />
                              {receiptFile ? (
                                <div className="flex items-center gap-2 text-green-600">
                                  <CheckCircle2 className="h-5 w-5" />
                                  <span className="text-sm">{receiptFile.name}</span>
                                </div>
                              ) : (
                                <>
                                  <Upload className="h-5 w-5 text-slate-400" />
                                  <span className="text-sm text-slate-500">Click to upload receipt</span>
                                </>
                              )}
                            </label>
                            {receiptPreview && (
                              <img src={receiptPreview} alt="Receipt preview" className="mt-2 max-h-32 rounded" />
                            )}
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleBankTransferSubmit}
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        disabled={processing || uploadingReceipt}
                        data-testid="submit-bank-transfer"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : uploadingReceipt ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading receipt...
                          </>
                        ) : (
                          "Submit for Verification"
                        )}
                      </Button>
                    </div>
                  )}

                  {/* FirstPay */}
                  {selectedMethod === "firstpay" && (
                    <div className="space-y-4" data-testid="firstpay-section">
                      {/* Instructions */}
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <Banknote className="h-4 w-4" />
                          FirstPay (CIBC FirstCaribbean)
                        </h3>
                        <p className="text-sm text-blue-700">
                          Complete your payment via CIBC FirstCaribbean&apos;s FirstPay service, 
                          then enter the transaction details below for verification.
                        </p>
                      </div>

                      {/* Payer Info Form */}
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="fp_payer_name">Your Name</Label>
                          <Input
                            id="fp_payer_name"
                            value={payerName}
                            onChange={(e) => setPayerName(e.target.value)}
                            placeholder="John Doe"
                            data-testid="input-fp-payer-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="fp_transaction_id">Transaction ID / Reference</Label>
                          <Input
                            id="fp_transaction_id"
                            value={transferReference}
                            onChange={(e) => setTransferReference(e.target.value)}
                            placeholder="Enter your FirstPay transaction ID"
                            data-testid="input-fp-transaction-id"
                          />
                        </div>
                        <div>
                          <Label htmlFor="fp_receipt">Upload Receipt (optional)</Label>
                          <div className="mt-1">
                            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 transition-colors">
                              <input
                                type="file"
                                id="fp_receipt"
                                className="hidden"
                                accept="image/*,.pdf"
                                onChange={handleFileChange}
                                data-testid="input-fp-receipt"
                              />
                              {receiptFile ? (
                                <div className="flex items-center gap-2 text-green-600">
                                  <CheckCircle2 className="h-5 w-5" />
                                  <span className="text-sm">{receiptFile.name}</span>
                                </div>
                              ) : (
                                <>
                                  <Upload className="h-5 w-5 text-slate-400" />
                                  <span className="text-sm text-slate-500">Click to upload receipt</span>
                                </>
                              )}
                            </label>
                            {receiptPreview && (
                              <img src={receiptPreview} alt="Receipt preview" className="mt-2 max-h-32 rounded" />
                            )}
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleFirstPaySubmit}
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        disabled={processing || uploadingReceipt}
                        data-testid="submit-firstpay"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : uploadingReceipt ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading receipt...
                          </>
                        ) : (
                          "Submit for Verification"
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-500">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                Secure payment • All transactions encrypted
              </div>
            </div>

            {/* Trust Badges */}
            <TrustBadges variant="checkout" className="mt-0" data-testid="checkout-trust-badges" />

            {/* Terms & Privacy Notice */}
            <p className="text-xs text-slate-500 text-center mt-4">
              By completing your purchase, you agree to our{" "}
              <a href="/terms" className="text-orange-600 hover:underline" target="_blank" rel="noopener noreferrer">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-orange-600 hover:underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
              All sales are final. Customs duties and taxes are mandatory.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
